/**
 * AI service API calls using authenticated HTTP client
 */

import { authenticatedHttpClient } from './httpClient';

// AI Mapping interfaces
export interface AIMapping {
  excelColumn: string;
  modelField: string;
  confidence: number;
  reasoning: string;
  required: boolean;
}

export interface AIMappingResponse {
  mappings: AIMapping[];
  unmappedExcelColumns: string[];
  unmappedModelFields: string[];
  overallConfidence: number;
  suggestions: string[];
}

export interface AIMappingRequest {
  excelColumns: string[];
  modelFields: Array<{
    key: string;
    label: string;
    required: boolean;
    dataType: 'string' | 'number' | 'boolean' | 'date' | 'email';
    description?: string;
    example?: string;
  }>;
  modelName: string;
  sampleData: Array<Record<string, any>>;
  language: string;
}

export interface IntelligenceProductStatus {
  productEnabled: boolean;
  preview?: boolean;
  capacityId?: 'lite' | 'plus' | 'extra' | null;
  creditsAllowance: number;
  creditsUsed: number;
  bonusCredits?: number;
  creditsRemaining: number;
  price?: number | null;
  currency?: string;
  periodEnd?: string | null;
  trialing?: boolean;
  trialAvailable?: boolean;
  hasUsedIntelligenceTrial?: boolean;
  paymentMethod?: string | null;
  paymentMethodsAllowed?: Array<'stripe' | 'ves' | 'binance' | string>;
  bundleOffer?: {
    capacityId: string;
    discountPercent: number;
    listPrice: number;
    discountedPrice: number;
    currency: string;
    name: string;
  } | null;
  catalog?: Array<{
    id: string;
    name: string;
    monthlyCredits: number;
    monthlyPrice: number;
    currency: string;
  }>;
  creditPacks?: Array<{
    id: string;
    name: string;
    credits: number;
    price: number;
    currency: string;
  }>;
  pendingVes?: Array<{
    requestId: string;
    product: string;
    productLabel: string;
    itemDescription: string;
    capacityId?: string | null;
    packId?: string | null;
    packName?: string | null;
    packCredits?: number | null;
    packQuantity?: number | null;
    amountUSD: number;
    amountVES: number;
    createdAt?: string | null;
  }>;
}

/**
 * Call AI service to automatically map Excel columns to model fields
 */
export const mapColumnsWithAI = async (
  request: AIMappingRequest
): Promise<AIMappingResponse> => {
  try {
    const response = await authenticatedHttpClient.post<AIMappingResponse>(
      '/ai/map-columns',
      request
    );
    return response;
  } catch (error: any) {
    console.error('AI mapping error:', error);
    throw new Error(
      error?.response?.data?.detail || 'Error al procesar la solicitud de IA'
    );
  }
};

export const fetchIntelligenceProductStatus = async (
  organizationId: string
): Promise<IntelligenceProductStatus> => {
  return authenticatedHttpClient.get<IntelligenceProductStatus>(
    `/ai/product-status?organizationId=${encodeURIComponent(organizationId)}`
  );
};
